import { MethodMarks } from '../method-marks';
import { MarkedProxyDecorator } from '../proxy.decorator';
import { DeadlineExceededError } from './deadline-exceeded.error';

const marks = new MethodMarks<DeadlineOverrides>('shared-decorators:deadlinedMethods');

/**
 * Marks a method as bounded in time, optionally with its own limit.
 * {@link DeadlineEnforcer} applies the bound at the composition root.
 *
 * **Async only, enforced by the compiler:** JavaScript cannot interrupt a synchronous
 * call, so a deadline on one is no guarantee at all. Prototype methods only.
 *
 * @example
 * class S3FileRepository implements FileRepository {
 *   @deadlined()
 *   public async read(path: string): Promise<Buffer> { ... }        // instance limit
 *
 *   @deadlined({ timeoutMs: 30_000 })
 *   public async copyFolder(path: string): Promise<void> { ... }    // legitimately slow
 * }
 */
export function deadlined(overrides?: DeadlineOverrides) {
  // Zero or NaN would reject every call — NaN because `setTimeout` reads it as 0.
  if (overrides && !(overrides.timeoutMs > 0)) throw new Error('[deadlined] timeoutMs must be greater than 0');

  // `descriptor` carries the `T` constraint — the only place a legacy method decorator
  // sees the method's type. Returned unchanged.
  return <T extends (...args: never[]) => Promise<unknown>>(
    prototype: object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<T>
  ): TypedPropertyDescriptor<T> => {
    marks.add(prototype, propertyKey, overrides);

    return descriptor;
  };
}

/**
 * The methods marked with {@link deadlined} on `target` or up its prototype chain.
 *
 * A delegating function rather than `= marks.of`: detaching a method loses `this`.
 */
export function deadlinedMethodsOf(target: object): ReadonlyMap<string, DeadlineOverrides | undefined> {
  return marks.of(target);
}

/**
 * Reports how much of the current operation's time budget is left.
 *
 * The budget belongs to the invocation while an enforcer is shared, so an implementation
 * must resolve the operation in progress on every call — `AsyncLocalStorage`, or a
 * request-scoped container.
 *
 * **No implementation ships here:** this barrel is bundled by the Angular app through
 * `@telespot/infrastructure/parse`, so nothing reachable from it may import
 * `node:async_hooks`. Provide the source from the Node composition root, as with
 * {@link ILogger} and {@link IMetrics}.
 *
 * @example
 * const operation = new AsyncLocalStorage<{ deadlineMs: number }>();
 *
 * const lambdaBudget: DeadlineSource = {
 *   remainingMs: () => {
 *     const scope = operation.getStore();
 *     return scope && scope.deadlineMs - Date.now();
 *   },
 * };
 *
 * // set once per invocation, minus a margin to answer and flush metrics
 * operation.run({ deadlineMs: Date.now() + context.getRemainingTimeInMillis() - 1_000 }, () => handler(event));
 */
export interface DeadlineSource {
  /** Milliseconds left, or `undefined` when no budget is in scope. May be <= 0. */
  remainingMs(): number | undefined;
}

/** How long a call may take, and how long the operation around it has left. */
export interface DeadlinePolicy {
  /**
   * Default limit for one call, in milliseconds. Must be > 0.
   *
   * Omit it to bound calls by `source` alone, for an operation whose own remaining time
   * is the only honest limit — a bulk write inside a long-running consumer.
   */
  timeoutMs?: number;
  /** Clamps every call to the operation's remaining budget. Optional. */
  source?: DeadlineSource;
}

/**
 * Per-method overrides on top of the instance {@link DeadlinePolicy}. `source` is excluded
 * structurally: a budget belongs to the invocation, not to a method spending it.
 */
export type DeadlineOverrides = Required<Pick<DeadlinePolicy, 'timeoutMs'>>;

/**
 * DeadlineEnforcer
 *
 * A proxy-based decorator bounding a target's {@link deadlined} methods by
 * `policy.timeoutMs` and — where `policy.source` supplies one — by the operation's
 * remaining budget, whichever is tighter. A policy may omit `timeoutMs` and bound calls
 * by the budget alone. Breaching either rejects with a {@link DeadlineExceededError}.
 * Unmarked methods are returned untouched, and it holds no mutable state, so one instance
 * can bound any number of targets.
 *
 * **The tighter bound decides retriability**, so a budget needs no cooperation from the
 * {@link Retrier}: outstaying its own limit is retriable, running the operation out of
 * time is not. Reaching a marked method with the budget already spent skips the call.
 *
 * **Abandoned, not cancelled.** The rejection releases the caller; the in-flight call runs
 * until the transport closes it. The ports these adapters implement have no parameter for
 * a cancellation signal.
 *
 * **Compose it inside a {@link Retrier} and outside a {@link RateLimiter}**: inside, a hung
 * attempt is replayed and every attempt re-reads the budget; outside, queue time counts
 * against the limit. {@link RateLimiter} cannot cancel a queued waiter, so a call abandoned
 * while queued still spends its token.
 *
 * @example
 * const deadline = new DeadlineEnforcer({ timeoutMs: 5_000, source: lambdaBudget });
 *
 * // innermost-first: translate, log, pace, bound, retry
 * export const s3Standard = new ProxyPipeline().use(translateS3Error, logCalls, rateLimit, deadline, retry);
 */
export class DeadlineEnforcer extends MarkedProxyDecorator<DeadlineOverrides> {
  constructor(private readonly policy: DeadlinePolicy) {
    super(marks);

    if (policy.timeoutMs !== undefined && !(policy.timeoutMs > 0))
      throw new Error('[DeadlineEnforcer] timeoutMs must be greater than 0');

    // Neither bound configured: every marked method would be wrapped and left unbounded.
    if (policy.timeoutMs === undefined && !policy.source)
      throw new Error('[DeadlineEnforcer] a policy without timeoutMs needs a source');
  }

  protected wrapMarked(
    method: (...args: unknown[]) => unknown,
    methodName: string,
    overrides: DeadlineOverrides | undefined
  ): (...args: unknown[]) => unknown {
    const timeoutMs = overrides?.timeoutMs ?? this.policy.timeoutMs;

    return async (...args: unknown[]) => {
      // Per call, not at wrap time: the budget belongs to the invocation, each retry
      // attempt included.
      const budgetMs = this.remainingBudgetMs();

      // Nothing left to spend, so the answer would arrive too late to use.
      if (budgetMs !== undefined && budgetMs <= 0) throw DeadlineExceededError.outOfBudget(methodName);

      const limitMs = this.tighterOf(timeoutMs, budgetMs);

      // No limit of its own and no budget in scope, so there is nothing to race.
      if (limitMs === undefined) return method(...args);

      return this.bound(method(...args), methodName, limitMs, limitMs !== timeoutMs);
    };
  }

  private remainingBudgetMs(): number | undefined {
    const remainingMs = this.policy.source?.remainingMs();

    // `undefined` is nothing in scope, `Infinity` an unbounded operation, `NaN` a bug that
    // would poison `Math.min` and fail every call. None gives a budget to clamp to.
    return remainingMs !== undefined && Number.isFinite(remainingMs) ? remainingMs : undefined;
  }

  /** The bound in force for one call, or `undefined` when neither side sets one. */
  private tighterOf(timeoutMs: number | undefined, budgetMs: number | undefined): number | undefined {
    if (timeoutMs === undefined) return budgetMs;
    if (budgetMs === undefined) return timeoutMs;

    return Math.min(timeoutMs, budgetMs);
  }

  /**
   * @param boundByBudget whether `limitMs` came from the budget rather than the method's
   *   own limit, which is what makes the breach terminal
   */
  private async bound(
    pending: unknown,
    methodName: string,
    limitMs: number,
    boundByBudget: boolean
  ): Promise<unknown> {
    // A synchronous return cannot be late; only reachable untyped.
    if (!(pending instanceof Promise)) return pending;

    let timer: ReturnType<typeof setTimeout> | undefined;

    const expiry = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () =>
          reject(
            boundByBudget
              ? DeadlineExceededError.outOfBudget(methodName, limitMs)
              : DeadlineExceededError.callTimedOut(methodName, limitMs)
          ),
        limitMs
      );
    });

    try {
      return await Promise.race([pending, expiry]);
    } catch (error) {
      // Nothing awaits the call once the deadline wins, and an unobserved rejection is
      // fatal under `--unhandled-rejections=strict`.
      void pending.catch(() => undefined);

      throw error;
    } finally {
      // An outstanding timer holds the event loop open.
      clearTimeout(timer);
    }
  }
}
